import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import { Flask, ArrowRight, Play, CheckCircle, Clock, ChartBar } from "@phosphor-icons/react";

const DIFFICULTY_LABEL = { easy: "Facile", medium: "Medio", hard: "Difficile" };
const DIFFICULTY_CLS = {
    easy: "bg-emerald-50 text-emerald-700 border-emerald-200",
    medium: "bg-yellow-50 text-yellow-800 border-yellow-200",
    hard: "bg-red-50 text-red-700 border-red-200",
};
const DECISION_CLS = {
    PROCEDERE: "bg-emerald-50 text-emerald-700",
    VALUTARE_CON_CAUTELA: "bg-yellow-50 text-yellow-800",
    APPROFONDIRE: "bg-blue-50 text-blue-700",
    SCARTARE: "bg-red-50 text-red-700",
};

function qualityColor(q) {
    if (q == null) return "text-gray-400";
    if (q >= 80) return "text-emerald-600";
    if (q >= 60) return "text-yellow-700";
    if (q >= 40) return "text-orange-600";
    return "text-red-600";
}

export default function ValidationLab() {
    const [golden, setGolden] = useState([]);
    const [evaluations, setEvaluations] = useState([]);
    const [filter, setFilter] = useState({ province: "", difficulty: "" });
    const [runningIds, setRunningIds] = useState(new Set());

    const load = async () => {
        const params = {};
        if (filter.province) params.province = filter.province;
        if (filter.difficulty) params.difficulty = filter.difficulty;
        const [g, ev] = await Promise.all([
            api.get("/validation/golden", { params }),
            api.get("/validation/golden-evaluations"),
        ]);
        setGolden(g.data);
        setEvaluations(ev.data);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter.province, filter.difficulty]);

    useEffect(() => {
        if (runningIds.size === 0) return;
        const t = setInterval(load, 5000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runningIds]);

    const runEvaluation = async (goldenId) => {
        setRunningIds((prev) => new Set(prev).add(goldenId));
        try {
            await api.post(`/validation/golden/${goldenId}/run`);
            setTimeout(() => {
                setRunningIds((prev) => {
                    const next = new Set(prev);
                    next.delete(goldenId);
                    return next;
                });
                load();
            }, 60000);
        } catch (e) {
            setRunningIds((prev) => {
                const next = new Set(prev);
                next.delete(goldenId);
                return next;
            });
        }
    };

    const latestEvalFor = (goldenId) =>
        evaluations.find((e) => e.golden_auction_id === goldenId);

    const totalScore =
        evaluations.length > 0
            ? evaluations.reduce((acc, e) => acc + (e.quality_score || 0), 0) / evaluations.length
            : null;

    return (
        <Layout>
            <div className="flex items-end justify-between mb-8" data-testid="validation-header">
                <div>
                    <div className="overline mb-2">Validation Lab</div>
                    <h1 className="font-display-black text-4xl sm:text-5xl leading-none">
                        Dataset Golden.
                    </h1>
                    <p className="text-sm text-gray-600 mt-3 max-w-xl">
                        Esegui la pipeline AI sui {golden.length} casi golden con verità umana.
                        Quality Score = 40% Risk Detection + 25% Market + 25% Decision + 10%
                        Completeness.
                    </p>
                </div>
                <Link
                    to="/kpi"
                    data-testid="goto-kpi-link"
                    className="hidden sm:flex h-12 px-5 items-center gap-2 border border-gray-300 hover:border-black text-sm font-semibold"
                >
                    <ChartBar size={16} weight="bold" />
                    Vai a KPI Dashboard
                </Link>
            </div>

            {/* HEADER KPI */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-gray-200 border border-gray-200 mb-8" data-testid="validation-kpi-strip">
                <KpiCell label="Casi golden" value={golden.length} />
                <KpiCell label="Valutazioni eseguite" value={evaluations.length} />
                <KpiCell label="Quality Score medio" value={totalScore != null ? `${totalScore.toFixed(1)}/100` : "—"} cls={qualityColor(totalScore)} />
                <KpiCell label="Difficoltà alta" value={golden.filter((g) => g.difficulty === "hard").length} />
            </div>

            {/* FILTERS */}
            <div className="flex gap-3 flex-wrap mb-6">
                <select
                    data-testid="filter-province"
                    value={filter.province}
                    onChange={(e) => setFilter({ ...filter, province: e.target.value })}
                    className="h-10 px-3 border border-gray-300 bg-white text-sm focus:border-black focus:outline-none"
                >
                    <option value="">Tutte le province</option>
                    <option value="Siena">Siena</option>
                    <option value="Firenze">Firenze</option>
                    <option value="Arezzo">Arezzo</option>
                </select>
                <select
                    data-testid="filter-difficulty"
                    value={filter.difficulty}
                    onChange={(e) => setFilter({ ...filter, difficulty: e.target.value })}
                    className="h-10 px-3 border border-gray-300 bg-white text-sm focus:border-black focus:outline-none"
                >
                    <option value="">Tutte le difficoltà</option>
                    <option value="easy">Facile</option>
                    <option value="medium">Medio</option>
                    <option value="hard">Difficile</option>
                </select>
                <BatchRunButton golden={golden} runEvaluation={runEvaluation} />
            </div>

            {/* TABLE */}
            <div className="bg-white border border-gray-200" data-testid="golden-table">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wider text-gray-500">
                        <tr>
                            <th className="text-left p-4 font-bold">Caso</th>
                            <th className="text-left p-4 font-bold">Provincia</th>
                            <th className="text-left p-4 font-bold">Difficoltà</th>
                            <th className="text-right p-4 font-bold">Human Score</th>
                            <th className="text-left p-4 font-bold">Decisione attesa</th>
                            <th className="text-right p-4 font-bold">Quality AI</th>
                            <th className="p-4"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {golden.map((g) => {
                            const ev = latestEvalFor(g.id);
                            const isRunning = runningIds.has(g.id);
                            return (
                                <tr key={g.id} data-testid={`golden-row-${g.id}`} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors">
                                    <td className="p-4">
                                        <div className="font-semibold">{g.title}</div>
                                        <div className="text-xs text-gray-500 mt-0.5">
                                            {g.property_type} • {g.comune}
                                        </div>
                                    </td>
                                    <td className="p-4 text-gray-700">{g.province}</td>
                                    <td className="p-4">
                                        <span className={`inline-block border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${DIFFICULTY_CLS[g.difficulty]}`}>
                                            {DIFFICULTY_LABEL[g.difficulty]}
                                        </span>
                                    </td>
                                    <td className="p-4 text-right font-mono-tight font-bold">{g.review?.human_score}/100</td>
                                    <td className="p-4">
                                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 ${DECISION_CLS[g.review?.investment_decision] || "text-gray-500"}`}>
                                            {(g.review?.investment_decision || "").replace(/_/g, " ")}
                                        </span>
                                    </td>
                                    <td className={`p-4 text-right font-mono-tight font-bold ${qualityColor(ev?.quality_score)}`}>
                                        {ev?.quality_score != null ? `${ev.quality_score.toFixed(1)}` : "—"}
                                    </td>
                                    <td className="p-4">
                                        <button
                                            data-testid={`run-eval-${g.id}`}
                                            onClick={() => runEvaluation(g.id)}
                                            disabled={isRunning}
                                            className="border border-gray-300 hover:border-black disabled:opacity-40 h-9 px-3 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5"
                                        >
                                            {isRunning ? (<><Clock size={12} weight="bold" />In corso</>) :
                                             ev ? (<><Play size={12} weight="bold" />Re-run</>) :
                                             (<><Play size={12} weight="bold" />Valuta</>)}
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* RECENT EVALS */}
            {evaluations.length > 0 && (
                <div className="mt-8 bg-white border border-gray-200 p-6" data-testid="recent-evaluations">
                    <div className="overline mb-3">Ultime valutazioni</div>
                    <ul className="divide-y divide-gray-100">
                        {evaluations.slice(0, 8).map((e) => {
                            const g = golden.find((x) => x.id === e.golden_auction_id);
                            return (
                                <li key={e.id} className="py-3 flex items-center justify-between text-sm">
                                    <div className="flex items-center gap-3">
                                        <CheckCircle size={14} weight="bold" className="text-emerald-600 flex-shrink-0" />
                                        <div>
                                            <div className="font-semibold">{g?.title || "—"}</div>
                                            <div className="text-xs text-gray-500 font-mono-tight">
                                                {new Date(e.created_at).toLocaleString("it-IT")}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-6">
                                        <Metric label="Risk F1" value={`${(((e.metrics?.legal_risk_f1 || 0) + (e.metrics?.urban_risk_f1 || 0)) / 2).toFixed(0)}`} />
                                        <Metric label="Market" value={`${(100 - (e.metrics?.market_valuation_mape || 100)).toFixed(0)}`} />
                                        <Metric label="Decision" value={`${e.metrics?.decision_match || 0}`} />
                                        <div className={`font-display-black text-xl ${qualityColor(e.quality_score)}`}>
                                            {e.quality_score?.toFixed(0)}
                                        </div>
                                        <Link to={`/auctions/?analysis=${e.analysis_id}`} className="text-gray-400 hover:text-black">
                                            <ArrowRight size={14} />
                                        </Link>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </Layout>
    );
}

function KpiCell({ label, value, cls = "" }) {
    return (
        <div className="bg-white p-5">
            <div className="overline mb-2">{label}</div>
            <div className={`font-display-black text-3xl ${cls}`}>{value}</div>
        </div>
    );
}

function Metric({ label, value }) {
    return (
        <div className="text-center">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
            <div className="font-mono-tight font-bold text-sm">{value}</div>
        </div>
    );
}

function BatchRunButton({ golden, runEvaluation }) {
    const [busy, setBusy] = useState(false);
    const runAll = async () => {
        setBusy(true);
        for (const g of golden) {
            runEvaluation(g.id);
            await new Promise((r) => setTimeout(r, 1500)); // stagger to avoid rate limits
        }
        setBusy(false);
    };
    return (
        <button
            data-testid="batch-run-button"
            onClick={runAll}
            disabled={busy || golden.length === 0}
            className="h-10 px-4 bg-black text-white text-xs font-bold uppercase tracking-wider hover:bg-gray-800 disabled:opacity-40 flex items-center gap-2"
        >
            <Flask size={14} weight="bold" />
            {busy ? "Avvio batch…" : `Valuta tutti (${golden.length})`}
        </button>
    );
}
